import moment from "moment";

import { CalendarEmployeeDisplay } from "@/Appointments/Models/Calendar/CalendarEmployeeDisplay";
import { CalendarDisplay } from "@/Appointments/Models/Settings/CalendarDisplay";
import { PAListDisplaySettings } from "@/Appointments/Models/Settings/PAListDisplaySettings";
import { Appointment as MyleModelsResponseCalendarAppointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Room } from "@/Common/Api/Appointment/Dto/Room";
import { INotificationContactPoint } from "@/Common/Api/Contacts/Dto/INotificationContactPoint";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { ToTargetedClienteleTranslatedValue } from "@/Common/Models/Services/Enums/ServiceComboTargetClientele";
import { ServiceModel } from "@/Common/Models/Services/ServiceModel";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { RoomUIModel } from "@/Common/Models/UI/RoomUIModel";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { AppointmentCountItem } from "../Api/Dto/Responses/AppointmentCountItem";
import { PredefinedAppointment } from "../Api/Dto/Responses/PredefinedAppointment";
import { PredefinedRuleSet } from "../Api/Dto/Responses/PredefinedRuleSet";
import { UpdatedAppointmentInformation } from "../Api/Dto/Responses/UpdatedAppointmentInformation";
import { CalendaHelper } from "../Helpers/CalendarHelper";
import { AppointmentContactPointNotification } from "../Models/Appointment/AppointmentContactPointNotification";
import { CalendarAppointmentDisplay } from "../Models/Calendar/CalendarAppointmentDisplay";
import { EnumRecurrenceType } from "../Models/EnumRecurrenceType";
import { RecurrenceType } from "../Models/RecurrenceType";
import { IAppointmentEditModel } from "../Models/UI/IAppointmentEditModel";
import { ICalendarDisplaySettingsModel } from "../Models/UI/ICalendarDisplaySettingsModel";
import { ICalendarModel } from "../Models/UI/ICalendarModel";
import { ICalendarPrintByDoctorModel } from "../Models/UI/ICalendarPrintByDoctorModel";
import { ICalendarPrintDialogModel } from "../Models/UI/ICalendarPrintDialogModel";
import { ICalendarRulesetModel } from "../Models/UI/ICalendarRulesetModel";
import { CalendarAction } from "./Actions/CalendarActions";
import { CalendarRealTimeNotificationHandler } from "./Handlers/CalendarRealTimeNotificationHandler";
import { AppointmentEditHandler } from "./Handlers/Local/Calendar/AppointmentEditHandler";
import { CalendarDisplaySettingsHandler } from "./Handlers/Local/Calendar/CalendarDisplaySettingsHandler";
import { CalendarHandler } from "./Handlers/Local/Calendar/CalendarHandler";
import { CalendarMiscHandler } from "./Handlers/Local/Calendar/CalendarMiscHandler";
import { CalendarPrintDialogsHandler } from "./Handlers/Local/Calendar/CalendarPrintDialogsHandler";
import { CalendarResponseHandler } from "./Handlers/Server/CalendarResponseHandler";

let defaultInstance: CalendarStore;

class CalendarStore extends BaseStore {
    private mapper: RequestMapper;
    private serverMapper: ServerResponseMapper;

    // ExpectedResponseStore: any;

    private calendarDisplaySettingsHandler: CalendarDisplaySettingsHandler;
    private calendarPrintDialogsHandler: CalendarPrintDialogsHandler;
    private calendarMiscHandler: CalendarMiscHandler;
    private calendarHandler: CalendarHandler;
    private appointmentEditHandler: AppointmentEditHandler;

    private serverDispatchHandler: CalendarResponseHandler;

    private defaultEditAppointmentModel = {
        Appointment: {},
        PredefinedAppointment: null,
        SelectedPatientId: null,
        SelectedPatient: [],
        PatientSearchString: "",
        PatientFamilyDoctorId: "",
        PatientFamilyDoctor: null,
        Subject: "",
        DefaultSubjects: [],
        SelectedSubjectServiceId: "",
        SelectedSubjectService: null,
        SelectedSubjectResourceDefinitionId: "",
        SelectedRoomId: "",
        Rooms: [],
        RoomText: "",
        RoomsDisplayText: [],
        SelectedSiteId: "",
        Sites: [],
        StartTime: new Date(),
        EndTime: new Date(),
        IsDateInvalid: false,

        SelectedIntervenors: [],
        Intervenors: [],
        AppointmentIntervenor: null,
        SelectedAppointmentStateCode: "", // SelectedApointmentStateCode: "",
        AppointmentState: [],
        Notes: "",
        PreviousNotes: "",
        ReferralDoctors: [],
        ReferralDoctorId: "",
        ReferralPhoneNumber: "",
        ReferralDoctorSearchString: "",
        SelectedRecurrenceType: "",
        RecurrenceTypes: [],
        RecurrenceEndDate: new Date(),
        IsPrintLabelOnSave: false,
        IsNewAppointment: false,
        IsFromPredefinedAppointment: false,
        IsFromCutAppointment: false,
        IsRecallsVisible: false,

        DurationForCutAndPaste: 0,

        IsPaid: false,
        IsTeleconsultation: false,
        NotifyPatient: false,
        NotifyPatientDisabled: true,
        ContactPointNotificationList: [],
        HasNotificationContactPointSpecified: false
    };

    private defaultPatientBannerModel = {
        Model: null,
        ModelUpdate: null,
        PatientDropdownVisible: false,
        Warnings: []
    };

    /**
     * Handles real-time calendar notifications from the server.
     */
    // @ts-ignore
    // ts-ignore is added to suppress the warning from noUnusedLocals errors in tsconfig
    private calendarRealTimeNotificationHandler: CalendarRealTimeNotificationHandler; // this registers its own registered operations, so it needs to be initialized even if it's not used directly
    public isSaving: boolean = false;
    public isNoticeBoard: boolean;

    public calendarModel: ICalendarModel;
    public calendarDisplaySettingsModel: ICalendarDisplaySettingsModel;
    public calendarPrintByDoctorModel: ICalendarPrintByDoctorModel;
    public calendarPrintDialogModel: ICalendarPrintDialogModel;
    public calendarRulesetModel: ICalendarRulesetModel;
    public appointmentEditModel: IAppointmentEditModel;

    public patientCreationModel: IPatientCreationModel;
    public patientPortalModel: Person;
    public patientBannerModel: IPatientBannerModel;
    public patientToPreloadOnNextAppCreation: Guid;
    public subject: string;

    // things required for table
    public columnManagerModel: Immutable.List<ColumnConfigurationModel>;

    public recurrenceTypes: RecurrenceType[];

    // following props used to store temp values in order to undo edit when clicking "cancel" on calendar settings
    // we store original selected ids to deal with shallow immutability of immutable.js ( if an object has mutable propertiess (ie: an array or Immutable.list) => these properties will still be mutable)
    public undoCalendarDisplaySettings: ICalendarDisplaySettingsModel;
    public undoSelectedEmployeesIds: string[];
    public undoSelectedRoomIds: string[];
    public undoSelectedSiteIds: string[];

    public calendarActions: CalendarAction;

    public helper: MyleHelper;

    public timeoutReferenceLoadAppointments: number;
    public timeoutReferenceLoadAppointmentsMonth: number;

    public readonly calendarsShownMin: number = 1;
    public readonly calendarsShownDefault: number = 20;
    public readonly calendarsShownMax: number = 20;

    constructor() {
        super();

        var self = this;

        this.timeoutReferenceLoadAppointments = -1;
        this.timeoutReferenceLoadAppointmentsMonth = -1;

        this.mapper = new RequestMapper();
        this.serverMapper = new ServerResponseMapper();

        this.helper = new MyleHelper();

        this.calendarActions = new CalendarAction();

        this.calendarHandler = new CalendarHandler();
        this.calendarDisplaySettingsHandler = new CalendarDisplaySettingsHandler();
        this.calendarPrintDialogsHandler = new CalendarPrintDialogsHandler();

        this.calendarMiscHandler = new CalendarMiscHandler();
        this.appointmentEditHandler = new AppointmentEditHandler();

        this.serverDispatchHandler = new CalendarResponseHandler(this.calendarActions);

        this.mapper.AddInstance(this.calendarHandler);
        this.mapper.AddInstance(this.calendarDisplaySettingsHandler);
        this.mapper.AddInstance(this.calendarPrintDialogsHandler);
        this.mapper.AddInstance(this.calendarMiscHandler);
        this.mapper.AddInstance(this.appointmentEditHandler);

        // map server responses here
        this.serverMapper.AddInstance(this.serverDispatchHandler);

        // real-time notification handler
        this.calendarRealTimeNotificationHandler = new CalendarRealTimeNotificationHandler(this);
        this.InitializeDefaultModels();

        // get display settings from cache
        var tbSettings: PAListDisplaySettings = cacheStore.UserSettings?.PAListDisplaySettings;

        if (tbSettings == null) {
            tbSettings = new PAListDisplaySettings();
        }

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => self.mapper.Map(request, self));

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => self.serverMapper.Map(response, self));
    }

    public static createSingleton(): CalendarStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new CalendarStore());
    }

    public static createSingletonFrom(store: CalendarStore, copy: boolean = true): CalendarStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    /********************************************************************************
     *  CALENDAR DISPLAY AND NAVIGATION
     *******************************************************************************/

    public LoadCalendarDisplaySettings(calendarDisplay: CalendarDisplay): void {
        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("IsAllSitesChecked", calendarDisplay.IsAllSitesChecked);
        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("IsAllRoomsChecked", calendarDisplay.IsAllRoomsChecked);
        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("IsAllEquipmentChecked", calendarDisplay.IsAllEquipmentChecked);

        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("Sites", this.helper.ChangeCheckStateFromAllICheckable(this.calendarDisplaySettingsModel.get("Sites"), false));
        if (calendarDisplay.SiteIds == null || calendarDisplay.SiteIds.length == 0)
            this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("Sites", this.helper.ChangeCheckStateFromAllICheckable(this.calendarDisplaySettingsModel.get("Sites"), true));
        else
            this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("Sites", this.helper.FindUpdateICheckableItems(this.calendarDisplaySettingsModel.get("Sites"), calendarDisplay.SiteIds, true));

        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("Rooms", this.helper.ChangeCheckStateFromAllICheckable(this.calendarDisplaySettingsModel.get("Rooms"), false));
        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("Rooms", this.helper.FindUpdateICheckableItems(this.calendarDisplaySettingsModel.get("Rooms"), calendarDisplay.RoomIds, true));
        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("EmployeesGlobalCache", this.helper.ChangeCheckStateFromAllICheckable(this.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), false));

        if (calendarDisplay.EmployeeIds.length > 0)
            this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("EmployeesGlobalCache", this.helper.FindUpdateICheckableItems(this.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), calendarDisplay.EmployeeIds, true));
        else
            this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("EmployeesGlobalCache", this.helper.FindUpdateICheckableItems(this.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), [sessionStore.individualContactId], true));

        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel
            .set("ShowOnlySelectedSites", calendarDisplay.ShowOnlySelectedSite)
            .set("SortByScheduleSelectedEmployees", calendarDisplay.SortByScheduleSelectedEmployees)
            .set("PatientsAreVisible", calendarDisplay.PatientsAreVisible)
            .set("StatusAreVisible", calendarDisplay.StatusAreVisible)
            .set("RoomsAreVisible", calendarDisplay.RoomsAreVisible)
            .set("ContactPointsAreVisible", calendarDisplay.ContactPointsAreVisible)
            .set("IncludeCanceled", calendarDisplay.IncludeCanceled)
            .set("IncludeNAM", calendarDisplay.IncludeNAM)
            .set("TimeSlotLength", calendarDisplay.TimeSlotLength)
            .set("Service5minAreVisible", calendarDisplay.Service5minAreVisible)
            .set("SitesAreVisible", calendarDisplay.SiteAreVisible)
            .set("AreRulesetsCollapsed", calendarDisplay.AreRulesetsCollapsed)
            .set("OneLineViewSelected", calendarDisplay.OneLineViewSelected)
            .set("MaximumCalendarsShownForDay", calendarDisplay.MaximumCalendarsShown?.Day)
            .set("MaximumCalendarsShownForWeek", calendarDisplay.MaximumCalendarsShown?.Week)
            .set("MaximumCalendarsShownForMonth", calendarDisplay.MaximumCalendarsShown?.Month)
            .set("EmployeesFiltered", CalendaHelper.GetEmployeesBasedOnSitesAndQuery(this.calendarDisplaySettingsModel.get("EmployeesSearchText")))
            .set("CanadianTriageAndAcuityScoreVisible", calendarDisplay.CanadianTriageAndAcuityScoreVisible);
    }

    public LoadCalendarPrintDialog(calendarDisplay: CalendarDisplay): void {
        this.calendarPrintByDoctorModel = this.calendarPrintByDoctorModel.set("Sites", this.helper.ChangeCheckStateFromAllICheckable(this.calendarPrintByDoctorModel.get("Sites"), false));
        if (calendarDisplay.SiteIds == null || calendarDisplay.SiteIds.length == 0)
            this.calendarPrintByDoctorModel = this.calendarPrintByDoctorModel.set("Sites", this.helper.ChangeCheckStateFromAllICheckable(this.calendarPrintByDoctorModel.get("Sites"), true));
        else
            this.calendarPrintByDoctorModel = this.calendarPrintByDoctorModel.set("Sites", this.helper.FindUpdateICheckableItems(this.calendarPrintByDoctorModel.get("Sites"), calendarDisplay.SiteIds, true));

        this.calendarPrintByDoctorModel = this.calendarPrintByDoctorModel.set("EmployeesGlobalCache", this.helper.ChangeCheckStateFromAllICheckable(this.calendarPrintByDoctorModel.get("EmployeesGlobalCache"), false));
        this.calendarPrintByDoctorModel = this.calendarPrintByDoctorModel.set("EmployeesGlobalCache", this.helper.FindUpdateICheckableItems(this.calendarPrintByDoctorModel.get("EmployeesGlobalCache"), calendarDisplay.EmployeeIds, true));
    }

    /**
     *  sets the displayed employees on the calendar page
     */
    private getGlobalCacheEmployeeDisplay(): CalendarEmployeeDisplay[] {
        const employeeModels = this.helper.GetCheckedItemsFromICheckableList(this.calendarDisplaySettingsModel.get("EmployeesGlobalCache")).toArray().map(x => <EmployeeUIModel>x);
        const employeeDisplay = employeeModels
            .map(employee => CalendarEmployeeDisplay.FromUIModel(employee))
            .sortByProperty(employee => employee.EmployeeLastName);

        const maxPerPage = this.GetMaximumCalendarsShown();
        const maxIndexZeroBase = Math.max(employeeDisplay.length - maxPerPage, 0);

        this.calendarModel = this.calendarModel.set("TotalEmployeePages", maxIndexZeroBase + 1);

        const currentIndexZeroBase = this.calendarModel.get("CurrentPageEmployeeIndex");
        if (currentIndexZeroBase >= maxIndexZeroBase) {
            this.calendarModel = this.calendarModel.set("CurrentPageEmployeeIndex", maxIndexZeroBase);
        }

        this.calendarModel = this.calendarModel.set("AllEmployees", Immutable.List<CalendarEmployeeDisplay>(employeeDisplay));

        return employeeDisplay;
    }

    private GetMaximumCalendarsShown(): number {
        let maximumCalendars: number;

        if (this.calendarModel.get("IsCalendarDayView"))
            maximumCalendars = this.calendarDisplaySettingsModel.get("MaximumCalendarsShownForDay");
        else if (this.calendarModel.get("IsCalendarWeekView"))
            maximumCalendars = this.calendarDisplaySettingsModel.get("MaximumCalendarsShownForWeek");
        else if (this.calendarModel.get("IsCalendarMonthView"))
            maximumCalendars = this.calendarDisplaySettingsModel.get("MaximumCalendarsShownForMonth");

        return Number(maximumCalendars || calendarStore.calendarsShownDefault);
    }

    public onEmployeePageChange(): void {
        const employees = this.calendarModel.get("AllEmployees").toArray();
        const maxPerPage = this.GetMaximumCalendarsShown();
        const currentPageIndex = this.calendarModel.get("CurrentPageEmployeeIndex");
        const displayedEmployees: CalendarEmployeeDisplay[] = employees.length <= maxPerPage
            ? employees
            : employees.slice(currentPageIndex, currentPageIndex + maxPerPage);

        this.calendarModel = this.calendarModel.set("DisplayedEmployees", Immutable.List<CalendarEmployeeDisplay>(displayedEmployees));
    }

    public SetCalendarEmployees(): void {
        const allEmployees = this.getGlobalCacheEmployeeDisplay();
        const employeesIds = cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting
            ? allEmployees.map(employee => employee.ContactFacetId)
            : null;

        if (this.calendarModel.get("CurrentPageEmployeeIndex") == null)
            this.calendarModel = this.calendarModel.set("CurrentPageEmployeeIndex", 0);

        this.onEmployeePageChange();

        this.calendarActions.LoadCalendarAppointmentCounts(
            this.calendarModel,
            this.calendarDisplaySettingsModel,
            () => this.calendarActions.LoadCalendar(this.calendarModel, this.calendarDisplaySettingsModel, null, employeesIds)
        );
    }

    public SetCalendarEmployeesNextPage(): void {
        var currentPageIndex = this.calendarModel.get("CurrentPageEmployeeIndex");

        if (currentPageIndex < this.calendarModel.get("TotalEmployeePages"))
            currentPageIndex++;

        this.calendarModel = this.calendarModel.set("CurrentPageEmployeeIndex", currentPageIndex);

        this.onEmployeePageChange();
    }

    public SetCalendarEmployeesPreviousPage(): void {
        var currentPageIndex = this.calendarModel.get("CurrentPageEmployeeIndex");
        currentPageIndex--;
        if (currentPageIndex < 0)
            currentPageIndex = 0;

        this.calendarModel = this.calendarModel.set("CurrentPageEmployeeIndex", currentPageIndex);

        this.onEmployeePageChange();
    }

    /**
     * Update and reloads a single appointment in the calendar.
     * @param reloadedAppointment The appointment to be reloaded in the calendar.
     */
    public ReloadSingleAppointmentInCalendar(reloadedAppointment: UpdatedAppointmentInformation): void {
        const currentAppointments = this.calendarModel.get("Appointments").toArray();
        const listOfMatchesForReloadedAppointment = currentAppointments.filter(p => p.AppointmentId === reloadedAppointment.AppointmentId);

        if (!listOfMatchesForReloadedAppointment.length || !this.ShouldAppointmentBeUpdated(reloadedAppointment, listOfMatchesForReloadedAppointment[0]))
            return;

        const unmodifedAppointments = currentAppointments.filter(p => p.AppointmentId !== reloadedAppointment.AppointmentId);
        // This enables updates for appointments with multiple intervenors
        for (const appointment of listOfMatchesForReloadedAppointment) {
            const lookup = cacheStore.GetLookupById(reloadedAppointment.AppointmentStateLookupId);
            appointment.AppointmentStateLookupId = reloadedAppointment.AppointmentStateLookupId;
            appointment.AppointmentStateLookupCode = lookup.LookupCode;
            appointment.AppointmentStateLookupName = lookup.LookupTypeName;
            appointment.StartTime = reloadedAppointment.StartTime;
            appointment.EndTime = reloadedAppointment.EndTime;
        }
        const updatedAppointments = unmodifedAppointments.concat(listOfMatchesForReloadedAppointment);
        this.calendarModel = this.calendarModel.set("Appointments", Immutable.List(updatedAppointments));
        this.SetCalendarAppointments();
    }

    public ReloadCalendarForEmployee(reloadedAppointments: MyleModelsResponseCalendarAppointment[], reloadedPredefinedAppointments: PredefinedAppointment[], reloadedRuleSets: PredefinedRuleSet[]): void {
        var currentAppointments = this.calendarModel.get("Appointments").toArray();
        var currentPredefinedAppointments = this.calendarModel.get("PredefinedAppointments").toArray();
        var currentRuleSets = this.calendarModel.get("PredefinedRuleSets").toArray();

        var reloadedAppointmentsEmployees: string[] = ArrayHelper.GetArrayOfUniques(reloadedAppointments.map(a => a.EmployeInactivableId));
        var reloadedPredefinedAppointmentsEmployees: string[] = ArrayHelper.GetArrayOfUniques(reloadedPredefinedAppointments.map(a => a.EmployeeId));
        var reloadedRuleSetsEmployees: string[] = (reloadedRuleSets != null) ? ArrayHelper.GetArrayOfUniques(reloadedRuleSets.map(a => a.EmployeeId)) : [];

        var employes = reloadedPredefinedAppointmentsEmployees.concat(reloadedAppointmentsEmployees).concat(reloadedRuleSetsEmployees);
        var reloadedEmployes: string[] = ArrayHelper.GetArrayOfUniques(employes);

        reloadedEmployes.forEach(employeeId => {
            // remove all appointments for that employee
            currentAppointments = currentAppointments
                .filter(a => a.EmployeInactivableId != employeeId);

            // remove all predefinedRuleSEts for that employee
            currentRuleSets = currentRuleSets
                .filter(a => a.EmployeeId != employeeId);

            // remove all predefinedappointments for that employee
            currentPredefinedAppointments = currentPredefinedAppointments
                .filter(a => a.EmployeeId != employeeId);
        });

        currentAppointments = currentAppointments.concat(reloadedAppointments);
        currentPredefinedAppointments = currentPredefinedAppointments.concat(reloadedPredefinedAppointments);
        currentRuleSets = currentRuleSets.concat(reloadedRuleSets);

        this.calendarModel = this.calendarModel.set("Appointments", Immutable.List(currentAppointments));
        this.calendarModel = this.calendarModel.set("PredefinedAppointments", Immutable.List(currentPredefinedAppointments));
        this.calendarModel = this.calendarModel.set("PredefinedRuleSets", Immutable.List(currentRuleSets));

        this.SetCalendarAppointments();
    }

    // this will convert response appointment objects to a displayable appointment objects in the calendar
    public SetCalendarAppointments(): void {
        var appointments = this.calendarModel.get("Appointments").map(x => {
            var app: CalendarAppointmentDisplay = new CalendarAppointmentDisplay();

            app.id = x.AppointmentId;
            app.AppointmentId = x.AppointmentId;
            app.AppointmentItemId = x.AppointmentItemId;

            if (x.AppointmentStateLookupCode != null) {
                app.AppointmentStatusCode = x.AppointmentStateLookupCode.replace(/\s+/g, "").toUpperCase();
            }

            if (x.AppointmentStateLookupId != null)
                app.AppointmentStatusLookupId = x.AppointmentStateLookupId;

            app.AppointmentStatus = cacheStore.GetLocalizedValueByLookupId(x.AppointmentStateLookupId);

            if (app.AppointmentStatus != null) {
                app.AppointmentStatus = app.AppointmentStatus.toUpperCase();
            }

            app.AppointmentSubject = x.Subject;
            app.AppointmentServiceName = x.FilteredResourcesServiceName;
            app.AppointmentDescription = cacheStore.GetAppointmentDescription(x.Subject, x.ServiceDefinitionId, x.ComboId);
            app.AppointmentDescriptionWithoutCombo = cacheStore.GetAppointmentDescription(x.Subject, x.ServiceDefinitionId, null);
            app.EmployeeContactFacetId = x.EmployeInactivableId;
            app.PatientId = x.PatientId;
            app.PatientContactId = x.PatientContactId;
            app.PatientName = NameHelper.formatFullNameForHasPatientName(x);

            if (x.PatientPhoneNumbers?.length > 0) {
                app.ContactPoints = x.PatientPhoneNumbers.map(p => PhoneNumber.stringFormattedPhone(p, true));
            }

            app.PatientNAM = x.PatientNAM;
            app.PatientIdentifier = (x.PatientIdentifier1 ? x.PatientIdentifier1 : "") + (x.PatientIdentifier2 ? ", " + x.PatientIdentifier2 : "") + (x.PatientIdentifier3 ? ", " + x.PatientIdentifier3 : "");
            app.start = moment(x.StartTime).toDate();
            app.end = moment(x.EndTime).toDate();
            app.SiteId = x.SiteId;
            app.CanadianTriageAndAcuityScore = x.CanadianTriageAndAcuityScore;

            var site = cacheStore.GetSiteById(x.SiteId);
            app.SiteName = site != null ? site.Model.ClinicalName : "";
            app.SiteCode = site != null ? site.Model.Acronym : "";

            if (x.IsPaid) {
                app.IsPaid = "$";
            }

            app.IsTeleconsultation = x.IsTeleconsultation;

            app.Room = x.Location;
            app.IsSharedWithPortal = x.IsSharedWithPortal;
            app.AppointmentComboId = x.AppointmentComboId;

            app.ServiceDefinitionId = x.ServiceDefinitionId;
            if (app.ServiceDefinitionId != null) {
                app.ServiceDefinitionName = cacheStore.GetServiceDefinitionName(app.ServiceDefinitionId);
            }

            app.ComboId = x.ComboId;
            if (app.ComboId != null) {
                app.ComboName = cacheStore.GetComboName(app.ComboId);
            }

            if (x.IsSharedWithPortal != null && x.IsSharedWithPortal == true) {
                app.PortalDisplayClass = x.PatientContactId != null ? "portal-shared" : "portal-nolink"; // class to be applied to portal appt to differenciate in calendar
            }

            if (x.IsExternallyBookedAppointment)
                app.PortalDisplayClass = x.PatientContactId ? "" : "portal-nolink";

            app.QuestionnaireStatusInfo = x.QuestionnaireStatusInfo && new QuestionnaireStatusInfo(x.QuestionnaireStatusInfo);

            if (x.QuebecBookingHubStatus != null) {
                app.IsQuebecHubBookedAppointment = true;
                app.QuebecBookingHubStatus = x.QuebecBookingHubStatus;
                app.QuebecBookingHubLastUpdated = x.QuebecBookingHubLastUpdated;
                app.QuebecBookingHubDisplayState = CalendaHelper.GetThirdPartySyncDisplayState(x.QuebecBookingHubStatus, app.QuebecBookingHubLastUpdated, x.LastUpdateDate);
            }

            app.IsSynchronized = x.IsSynchronized;
            app.HasPatient = x.HasPatient;
            app.IsReadOnly = x.IsReadOnly;

            if (x.PatientPopulationBasedFundingStatus) {
                app.PatientPopulationBasedFundingStatus = x.PatientPopulationBasedFundingStatus;
                app.PatientProviderContactFacetId = Patient.FromLoadAppointment(x)?.GetAttendingDoctorOrSpecialistOnfileIntervenor()?.Employee?.ContactFacetId;
            }

            return app;
        });
        this.calendarModel = this.calendarModel.set("AppointmentsDisplay", Immutable.List<CalendarAppointmentDisplay>(appointments));

        var predefinedAppointments = this.calendarModel.get("PredefinedAppointments").map(x => {
            var app: CalendarAppointmentDisplay = new CalendarAppointmentDisplay();

            app.id = x.Id;
            app.AppointmentId = x.Id;
            app.AppointmentStatusCode = "PREDEFINED";
            app.EmployeeContactFacetId = x.EmployeeId;
            app.PatientName = x.Name;
            app.start = moment(x.Start).toDate();
            app.end = moment(x.Start).add(x.Duration / (60 * 10000000), "minutes").toDate();

            var site = cacheStore.GetSiteById(x.SiteId);

            app.SiteId = x.SiteId;
            app.SiteName = site && site.Model.ClinicalName;
            app.SiteCode = site && site.Model.ClinicalCode;
            app.IsOnlineBookingAllowed = x.IsOnlineBookingAllowed;
            app.TargetedClientName = ToTargetedClienteleTranslatedValue(x.TargetedClient);

            let questionnaireStatusInfo = this.getQuestionnaireStatusInfoFromQuestionnaireAssociation(x.ServiceDefinitionId);
            app.QuestionnaireStatusInfo = questionnaireStatusInfo && new QuestionnaireStatusInfo(questionnaireStatusInfo);

            return app;
        });

        this.calendarModel = this.calendarModel.set("PredefinedAppointmentsDisplay", Immutable.List<CalendarAppointmentDisplay>(predefinedAppointments));

        var rulesetAppointments = this.calendarModel.get("PredefinedRuleSets").map(x => {
            var app: CalendarAppointmentDisplay = new CalendarAppointmentDisplay();
            app.FromRuleSet(x);
            return app;
        });
        this.calendarModel = this.calendarModel.set("PredefinedRuleSetsDisplay", Immutable.List<CalendarAppointmentDisplay>(rulesetAppointments));

        this.SetAppointmentNumbersPerEmployee();
    }

    // this will convert response appointment objects to a displayable appointment objects in the calendar
    public setCalendarAppointmentCounts(appointmentCounts: AppointmentCountItem[]): void {
        const appointments: CalendarAppointmentDisplay[] = [];

        function createAppointment(appCountItem: AppointmentCountItem, code: string): CalendarAppointmentDisplay {
            const appt = new CalendarAppointmentDisplay();
            appt.EmployeeContactFacetId = appCountItem.EmployeeId;
            appt.AppointmentStatusCode = code;
            appt.start = moment(appCountItem.Date).startOf("day").add(14, "hours").toDate();
            appt.end = moment(appCountItem.Date).startOf("day").add(23, "hours").toDate();
            return appt;
        }

        for (const appCount of appointmentCounts) {
            if (appCount.NbAppointments > 0 || (appCount.NbAppointmentFromPredefined > 0 && appCount.NbAvailabilities === 0)) {
                const bookedApps = createAppointment(appCount, "MONTHBOOKED");
                bookedApps.NbAppointments = appCount.NbAppointments + (appCount.NbAvailabilities === 0 ? appCount.NbAppointmentFromPredefined : 0);
                appointments.push(bookedApps);
            }

            if (appCount.NbAppointmentFromPredefined >= 0 && appCount.NbAvailabilities > 0) {
                const availableApps = createAppointment(appCount, "MONTHAVAILABLE");
                availableApps.NbAppointments = appCount.NbAppointmentFromPredefined;
                availableApps.NbAvailabilities = appCount.NbAvailabilities ?? 0;
                appointments.push(availableApps);
            }

            if (appCount.NbCancelledAppointments > 0 && this.calendarDisplaySettingsModel.get("IncludeCanceled")) {
                const canceledApps = createAppointment(appCount, "CANCELED");
                canceledApps.NbAppointments = appCount.NbCancelledAppointments;
                canceledApps.end = moment(appCount.Date).startOf("day").add(14, "hours").toDate();
                appointments.push(canceledApps);
            }

            if (appCount.MaxRulesetAvailability > 0) {
                const ruleSetApps = createAppointment(appCount, "RULESETAVAILABILITY");
                ruleSetApps.MaxRulesetAvailability = appCount.MaxRulesetAvailability;
                ruleSetApps.end = moment(appCount.Date).startOf("day").add(14, "hours").toDate();
                appointments.push(ruleSetApps);
            }
        }
        if (this.calendarModel.get("IsCalendarMonthView")) {
            this.calendarModel = this.calendarModel.set("AppointmentsDisplay", Immutable.List<CalendarAppointmentDisplay>(appointments));
            this.calendarModel = this.calendarModel.set("PredefinedAppointmentsDisplay", Immutable.List<CalendarAppointmentDisplay>([]));
        }

        this.SetAllEmployeesAppointmentCounts(appointments);

        if (cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting)
            this.orderEmployeesByAppointmentAndAvailabilities(appointments);
    }

    /**
        this will calculate current number of appointments and availabilities for each displayed doctor
    **/
    public SetAppointmentNumbersPerEmployee(): void {
        const displayedEmployees = this.calendarModel.get("DisplayedEmployees").toArray();
        const appointments = this.calendarModel.get("AppointmentsDisplay").toArray();
        const predefinedAppointments = this.calendarModel.get("PredefinedAppointments").toArray();
        const predefinedRulesets = this.calendarModel.get("PredefinedRuleSets").toArray();

        const appointmentsMap = new Map<string, number>();
        const availabilityMap = new Map<string, number>();
        const scheduledRulesetMap = new Map<string, number>();

        function increment(map: Map<string, number>, key: string): void {
            const oldValue = map.get(key) ?? 0;
            map.set(key, oldValue + 1);
        }

        for (const employee of displayedEmployees) {
            const contactFacetId = employee.ContactFacetId;
            appointmentsMap.set(contactFacetId, 0);
            availabilityMap.set(contactFacetId, 0);
        }

        for (const display of appointments) {
            const appointment = this.calendarModel.get("Appointments").find(p => p.AppointmentId === display.AppointmentId);

            if (!appointment) {
                console.warn(`No appointment was found for displayed appointment ID ${display.AppointmentId}.`);
                continue;
            }

            if (appointment.HasPatient) {
                const employeeId = GuidHelper.AsGuid(display.EmployeeContactFacetId);

                increment(appointmentsMap, employeeId);

                if (appointment.AppointmentIntervenorIds?.includes(employeeId)) {
                    increment(appointmentsMap, employeeId);
                }
            }
        }

        for (const predefinedAppointment of predefinedAppointments) {
            increment(availabilityMap, predefinedAppointment.EmployeeId);
        }

        for (const predefinedRuleset of predefinedRulesets) {
            increment(scheduledRulesetMap, predefinedRuleset.EmployeeId);
        }

        for (const employee of displayedEmployees) {
            const contactFacetId = employee.ContactFacetId;
            employee.AppointmentCount = appointmentsMap.get(contactFacetId) || 0;
            employee.AvailabilityCount = availabilityMap.get(contactFacetId) || 0;
            employee.HasScheduledRuleset = !!scheduledRulesetMap.get(contactFacetId);
        }

        this.calendarModel = this.calendarModel.set("DisplayedEmployees", Immutable.List(displayedEmployees));
    }

    /**
        this will calculate current number of appointments and availabilities for each doctor
    **/
    public SetAllEmployeesAppointmentCounts(appointments: CalendarAppointmentDisplay[]): void {
        const allEmployees = this.getGlobalCacheEmployeeDisplay();

        for (const appointment of appointments) {
            const employeeId = appointment.EmployeeContactFacetId;
            const employee = allEmployees.find(e => e.ContactFacetId === employeeId);

            if (employee) {
                if (appointment.NbAppointments) {
                    employee.AppointmentCount ??= 0;
                    employee.AppointmentCount += appointment.NbAppointments;
                }
                if (appointment.NbAvailabilities) {
                    employee.AvailabilityCount ??= 0;
                    employee.AvailabilityCount += appointment.NbAvailabilities;
                }
                if (appointment.MaxRulesetAvailability) {
                    employee.HasScheduledRuleset = !!appointment.MaxRulesetAvailability;
                }
            }
        }

        this.calendarModel = this.calendarModel.set("AllEmployees", Immutable.List(allEmployees));
        this.onEmployeePageChange();
    }

    private orderEmployeesByAppointmentAndAvailabilities(appointments: CalendarAppointmentDisplay[]): void {
        const sortByScheduleSelectedEmployees = this.calendarDisplaySettingsModel.get("SortByScheduleSelectedEmployees");

        if (!sortByScheduleSelectedEmployees)
            return;

        const allEmployees = this.calendarModel.get("AllEmployees").toArray();

        const appointmentsMap = new Map<string, number>();
        const availabilityMap = new Map<string, number>();
        const schedulingRuleMap = new Map<string, boolean>();

        for (const employee of allEmployees) {
            appointmentsMap.set(employee.ContactFacetId, 0);
            availabilityMap.set(employee.ContactFacetId, 0);
        }

        for (const appointment of appointments) {
            const employeeId = appointment.EmployeeContactFacetId;
            const employee = allEmployees.find(e => e.ContactFacetId === employeeId);

            if (!employee)
                continue;

            if (appointment.NbAppointments)
                appointmentsMap.set(employeeId, (appointmentsMap.get(employeeId) || 0) + appointment.NbAppointments);

            if (appointment.NbAvailabilities)
                availabilityMap.set(employeeId, (availabilityMap.get(employeeId) || 0) + appointment.NbAvailabilities);

            if (appointment.MaxRulesetAvailability)
                schedulingRuleMap.set(employeeId, !!appointment.MaxRulesetAvailability);
        }

        const { employeesAppointments, remainingEmployees } = allEmployees.reduce((acc, emp) => {
            const appointmentsCount = appointmentsMap.get(emp.ContactFacetId) || 0;
            const availabilityCount = availabilityMap.get(emp.ContactFacetId) || 0;
            const hasSchedulingRuleset = !!schedulingRuleMap.get(emp.ContactFacetId);

            if ((appointmentsCount + availabilityCount > 0) || hasSchedulingRuleset)
                acc.employeesAppointments.push(emp);
            else
                acc.remainingEmployees.push(emp);

            return acc;
        }, { employeesAppointments: [], remainingEmployees: [] });

        const sortedEmployees = [...employeesAppointments, ...remainingEmployees];

        this.calendarModel = this.calendarModel.set("AllEmployees", Immutable.List(sortedEmployees));
        this.onEmployeePageChange();
    }

    /**
     * Check if an appointment should be updated.
     * @param updatedAppointment The appointment with the updated information.
     * @param currentAppointment The appointment currently shown in the calendar.
     */
    private ShouldAppointmentBeUpdated(updatedAppointment: UpdatedAppointmentInformation, currentAppointment: MyleModelsResponseCalendarAppointment): boolean {
        if (!currentAppointment)
            return false;

        return (
            updatedAppointment.StartTime !== currentAppointment.StartTime
            || updatedAppointment.EndTime !== currentAppointment.EndTime
            || updatedAppointment.AppointmentStateLookupId !== currentAppointment.AppointmentStateLookupId
        );
    }

    /*******************************************************************************
      APPOINTMENT AND PATIENT EDITION
    *******************************************************************************/

    /**            sets an existing appointment model for edition in dialog        **/
    public GetAppointmentEditModel(appointment: MyleModelsResponseCalendarAppointment): IAppointmentEditModel {
        let editModel: IAppointmentEditModel = Immutable.fromJS(this.defaultEditAppointmentModel);
        const employee = cacheStore.GetEmployeeByContactFacetId(appointment.EmployeeId);

        const siteRooms = this.GetRoomsToDisplay(appointment.SiteId);

        editModel = editModel
            .set("Appointment", appointment)
            .set("SelectedPatientId", appointment.PatientId)
            .set("IsNewAppointment", false)
            .set("PatientSearchString", appointment.PatientId ? null : appointment.PatientNameString)
            .set("Subject", appointment.Subject)
            .set("DefaultSubjects", this.GetDefaultServicesBySiteAndEmployee(appointment.SiteId, employee))
            .set("SelectedRoomId", appointment.RoomId)
            .set("SelectedSiteId", appointment.SiteId)
            .set("Rooms", siteRooms)
            .set("RoomsDisplayText", Immutable.List(siteRooms.toArray().map(x => x.Name)))
            .set("RoomText", appointment.Location)
            .set("Sites", Immutable.List(cacheStore.SiteCache))
            .set("SelectedAppointmentStateCode", appointment.AppointmentStateLookupCode)
            .set("AppointmentStates", Immutable.List(cacheStore.GetAppointmentStateLookups()))
            .set("PreviousNotes", appointment.Notes)
            .set("Intervenors", Immutable.List(cacheStore.GetCliniciansWithClinicalEmployeeType()))
            .set("AppointmentIntervenor", cacheStore.GetCliniciansPersonsByContactFacetId(appointment.EmployeeId))
            .set("StartTime", appointment.StartTime)
            .set("EndTime", appointment.EndTime)
            .set("IsPaid", appointment.IsPaid)
            .set("IsTeleconsultation", appointment.IsTeleconsultation)
            .set("SelectedCanadianTriageAndAcuityScore", appointment.CanadianTriageAndAcuityScore)
            .set("IsCalendarEventTab", !appointment.HasPatient && cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior);

        if (appointment.PatientIndividual) {
            const patients = Immutable.List([Person.FromLoadAppointment(appointment)]);
            editModel = editModel.set("SelectedPatient", patients);
        }

        if (appointment.AppointmentIntervenorIds) {
            const intervenors = appointment.AppointmentIntervenorIds.map(id =>
                cacheStore.GetCliniciansPersonsByContactFacetId(id)
            );
            editModel = editModel.set("SelectedIntervenors", Immutable.List<Person>(intervenors));
        }

        let selectedService: ServiceModel = null;
        if (appointment.FilteredResourceId != null) {
            selectedService = new ServiceModel(
                appointment.FilteredResourceId,
                cacheStore.GetServiceDefinitionById(appointment.ServiceDefinitionId ?? appointment.FilteredResourceDefinitionId)
            );

            // the trimming logic is duplicated in ~\Myle.Api.RequestHandlers\Myle.Api.Public\Helpers\AppointmentHelper.cs - TrimServiceNameFromSubject
            if (selectedService != null && !selectedService.ServiceDefinitionModel.IsAdHoc) {
                editModel = editModel
                    .set("SelectedSubjectService", selectedService)
                    .set("SelectedSubjectServiceId", selectedService.ServiceID)
                    .set("SelectedSubjectResourceDefinitionId", selectedService.ServiceDefinitionModel.Model.Id)
                    .set("SelectedSubjectService", selectedService);
            }
        }

        editModel = editModel
            .set("RecurrenceTypes", Immutable.List(this.recurrenceTypes))
            .set("SelectedRecurrenceType", EnumRecurrenceType.OneTime)
            .set("RecurrenceEndDate", moment().toDate())
            .set("ReferralPhoneNumber", appointment.ReferralPhone);

        if (appointment.Referral) {
            const person = Person.FromMedfarDictionnaryLicence(appointment.Referral);
            editModel = editModel
                .set("ReferralDoctors", Immutable.List<Person>([person]))
                .set("ReferralDoctorId", appointment.ReferralId)
                .set("ReferralOrganizationRuleId", appointment.ReferralOrganizationRuleId);
        }
        else {
            const doctorName = appointment.CustomReferralDoctorName;

            if (doctorName) {
                const person = new Person({ Name: doctorName });
                editModel = editModel
                    .set("ReferralDoctors", Immutable.List<Person>([person]))
                    .set("ReferralDoctorSearchString", doctorName)
                    .set("ReferralOrganizationRuleId", appointment.ReferralOrganizationRuleId);
            }
            else {
                editModel = editModel
                    .set("ReferralDoctorSearchString", "")
                    .set("ReferralPhoneNumber", "")
                    .set("ReferralDoctors", Immutable.List([]))
                    .set("ReferralDoctorId", "")
                    .set("ReferralOrganizationRuleId", "");
            }
        }

        this.clearPatientToPreloadOnNextAppCreation();

        editModel = editModel
            .set("ContactPointNotificationList", appointment.ContactPointNotificationList.toImmutable())
            .set("HasNotificationContactPointSpecified", appointment.HasNotificationContactPointSpecified)
            .set("Questionnaire", appointment.QuestionnaireStatusInfo)
            .set("OriginalPatientContactId", appointment.PatientId)
            .set("OriginalQuestionnaire", appointment.QuestionnaireStatusInfo)
            .set("OriginalStartTime", appointment.StartTime);

        return editModel;
    }

    /**            returns a model with existing predefined appointment data for edition in dialog        **/
    public GetPredefinedAppointmentEditModel(appointment: PredefinedAppointment): IAppointmentEditModel {
        var editModel: IAppointmentEditModel = Immutable.fromJS(this.defaultEditAppointmentModel);

        var employee = cacheStore.GetEmployeeByContactFacetId(appointment.EmployeeId);

        editModel = editModel.set("IsNewAppointment", true); // CreateNew
        editModel = editModel.set("PredefinedAppointment", appointment);

        editModel = editModel.set("IsFromPredefinedAppointment", true);

        editModel = editModel.set("DefaultSubjects", this.GetDefaultServicesBySiteAndEmployee(appointment.SiteId, employee));
        editModel = editModel.set("SelectedRoomId", appointment.RoomId);
        editModel = editModel.set("SelectedSiteId", appointment.SiteId);
        editModel = editModel.set("Sites", Immutable.List(cacheStore.SiteCache));
        editModel = editModel.set("SelectedAppointmentStateCode", "UNCONFIRMED");
        editModel = editModel.set("AppointmentStates", Immutable.List(cacheStore.GetAppointmentStateLookups()));
        editModel = editModel.set("PreviousNotes", "");

        editModel = editModel.set("SelectedIntervenors", Immutable.List([cacheStore.GetCliniciansPersonsByContactFacetId(appointment.EmployeeId)]));
        editModel = editModel.set("Intervenors", Immutable.List(cacheStore.Clinicians));
        editModel = editModel.set("AppointmentIntervenor", cacheStore.GetCliniciansPersonsByContactFacetId(appointment.EmployeeId));

        editModel = editModel.set("StartTime", appointment.Start);
        editModel = editModel.set("EndTime", moment(appointment.Start).add(appointment.Duration / (60 * 10000000), "minutes").toDate());

        editModel = editModel.set("RecurrenceTypes", Immutable.List(this.recurrenceTypes));
        editModel = editModel.set("SelectedRecurrenceType", EnumRecurrenceType.OneTime);
        editModel = editModel.set("RecurrenceEndDate", moment().toDate());

        editModel = editModel.set("SelectedSubjectService", this.GetDefaultServicesBySiteAndEmployee(appointment.SiteId, employee).filter(c => c.ServiceDefinitionModel.Model.Id === appointment.ServiceDefinitionId)[0]);
        editModel = editModel.set("SelectedSubjectResourceDefinitionId", appointment.ServiceDefinitionId);

        const service = cacheStore.ServiceDefinitionCache.toArray().firstOrDefault(v => v.Model.Id === appointment.ServiceDefinitionId);
        editModel = editModel.set("IsTeleconsultation", service?.GetIsTeleconsultation() ?? false);

        let questionnaireStatusInfo = this.getQuestionnaireStatusInfoFromQuestionnaireAssociation(appointment.ServiceDefinitionId);
        editModel = editModel.set("Questionnaire", questionnaireStatusInfo)
            .set("OriginalQuestionnaire", questionnaireStatusInfo);

        if (this.patientToPreloadOnNextAppCreation != null) {
            editModel = editModel.set("SelectedPatientId", this.patientToPreloadOnNextAppCreation);
            calendarStore.calendarActions.LoadPatient(editModel);
            if (!IsNullOrWhiteSpace(this.subject)) {
                editModel = editModel.set("Subject", this.subject);
            }
        }
        else {
            editModel = editModel.set("SelectedPatientId", null);
            editModel = editModel.set("Subject", "");
        }
        return editModel;
    }

    /**            sets a new appointment model for edition in dialog        **/
    public SetNewAppointment(employeeId: Guid, startTime: Date): void {
        this.SetDefaultEditAppointmentModel();

        var lengthMinutes = this.calendarDisplaySettingsModel.get("TimeSlotLength");
        var endTime = moment(startTime).add(lengthMinutes, "minutes").toDate();
        var employee = cacheStore.GetEmployeeByContactFacetId(employeeId);
        const selectedService = this.appointmentEditModel.get("SelectedSubjectService");

        this.appointmentEditModel = this.appointmentEditModel
            .set("IsCalendarEventTab", false)
            .set("IsNewAppointment", true) // CreateNew
            .set("StartTime", startTime)
            .set("EndTime", endTime)

            .set("SelectedIntervenors", Immutable.List([cacheStore.GetCliniciansPersonsByContactFacetId(employeeId)]))
            .set("Intervenors", Immutable.List(cacheStore.GetCliniciansWithClinicalEmployeeType()))
            .set("AppointmentIntervenor", cacheStore.GetCliniciansPersonsByContactFacetId(employeeId))

            .set("Sites", Immutable.List(cacheStore.SiteCache))

            .set("IsTeleconsultation", selectedService != null && selectedService.ServiceDefinitionModel.Model.IsTeleconsultation);

        // find adhoc service in default site (else any site) for appointment intervenor
        var adhocService = employee.ServicesOffered.filter(x => x.ServiceDefinitionModel.Model.SiteId === employee.ActiveSiteId && x.ServiceDefinitionModel.IsAdHoc == true);
        if (adhocService == null || adhocService.length == 0)
            adhocService = employee.ServicesOffered.filter(x => x.ServiceDefinitionModel.IsAdHoc == true);

        var CurrentEmployeeSiteId = sessionStore.getCurrentEmployee().Model.ActiveSiteId;

        let defaultSite = CurrentEmployeeSiteId;
        if (employee.AssociatedSites != null && employee.AssociatedSites.length > 0)
            defaultSite = employee.AssociatedSites.firstPreferredOrDefault(x => x == CurrentEmployeeSiteId);

        let defaultServices = this.GetDefaultServicesBySiteAndEmployee(defaultSite, employee);

        this.appointmentEditModel = this.appointmentEditModel.set("SelectedSiteId", defaultSite);
        this.appointmentEditModel = this.appointmentEditModel.set("DefaultSubjects", defaultServices);

        this.appointmentEditModel = this.appointmentEditModel.set("Rooms", this.GetRoomsToDisplay());
        this.appointmentEditModel = this.appointmentEditModel.set("RoomsDisplayText", Immutable.List(this.GetRoomsToDisplay().toArray().map(x => x.Name)));

        this.appointmentEditModel = this.appointmentEditModel.set("SelectedAppointmentStateCode", "UNCONFIRMED");
        this.appointmentEditModel = this.appointmentEditModel.set("AppointmentStates", Immutable.List(cacheStore.GetAppointmentStateLookups()));

        this.appointmentEditModel = this.appointmentEditModel.set("SelectedRecurrenceType", EnumRecurrenceType.OneTime);
        this.appointmentEditModel = this.appointmentEditModel.set("RecurrenceEndDate", moment().toDate());

        this.calendarModel = this.calendarModel.set("IsEditAppointmentDialogVisible", true);
        this.appointmentEditModel = this.appointmentEditModel.set("SelectedPatientId", null);
        this.appointmentEditModel = this.appointmentEditModel.set("Subject", "");

        var tmpAppointment = new MyleModelsResponseCalendarAppointment();
        tmpAppointment.EmployeeId = employeeId;
        this.appointmentEditModel = this.appointmentEditModel.set("Appointment", tmpAppointment);

        if (this.patientToPreloadOnNextAppCreation != null) {
            this.appointmentEditModel = this.appointmentEditModel.set("SelectedPatientId", this.patientToPreloadOnNextAppCreation);
            calendarStore.calendarActions.LoadPatient(this.appointmentEditModel);
            if (!IsNullOrWhiteSpace(this.subject)) {
                this.appointmentEditModel = this.appointmentEditModel.set("Subject", this.subject);
            }
        }
        else {
            this.appointmentEditModel = this.appointmentEditModel.set("SelectedPatientId", null);
            this.appointmentEditModel = this.appointmentEditModel.set("Subject", "");
        }
    }

    public refreshNotifyPatientStatus(forceDefaultNotificationReset: boolean = false): void {
        let editModel = this.appointmentEditModel;
        let isNotifyAlreadyEnabled = editModel.get("Appointment")?.IsPatientNotificationsEnabled;

        if (isNotifyAlreadyEnabled) {
            // Existing appointment with notification already sent before. Lock to checked
            this.appointmentEditModel = editModel
                .set("NotifyPatient", true)
                .set("NotifyPatientDisabled", true);
            return;
        }

        const notifyPatient = this.shouldNotifyPatient(editModel);
        const disableNotifyPatient = !this.patientHasNotifiableContactPoint() || editModel.get("IsTeleconsultation");

        this.appointmentEditModel = editModel
            .set("NotifyPatient", notifyPatient)
            .set("NotifyPatientDisabled", disableNotifyPatient)
            .set("NotifyPatientManualOverride", null);

        if (this.appointmentEditModel.get("IsNewAppointment") || forceDefaultNotificationReset)
            this.handlePatientNotifications(this.patientBannerModel?.get("Model"), this.patientBannerModel);
    }

    public shouldNotifyPatient(editModel: IAppointmentEditModel): boolean {
        const notifyIsDisabledInService = editModel.get("SelectedSubjectService")
            ?.ServiceDefinitionModel
            ?.Model
            ?.IsNotifyPatientDisabledByDefault ?? false;

        if (editModel.get("IsTeleconsultation"))
            return true;

        if (editModel.get("NotifyPatientManualOverride") != null)
            return editModel.get("NotifyPatientManualOverride");

        if (editModel.get("Appointment")?.IsPatientNotificationsEnabled == null && editModel.get("SelectedSubjectService") && editModel.get("SelectedPatient")?.get(0))
            return !notifyIsDisabledInService;

        return false;
    }

    public SetDefaultEditAppointmentModel(): void {
        this.appointmentEditModel = Immutable.fromJS(this.defaultEditAppointmentModel);
        this.SetRecurrenceTypes();

        this.appointmentEditModel = this.appointmentEditModel.set("RecurrenceTypes", Immutable.List(this.recurrenceTypes));
        this.SetDefaultPatientBannerModel();
    }

    public SetDefaultPatientModel(): void {
        var defaultPatientCreationModel = PatientHelper.getDefaultPatientCreationModel();

        this.patientCreationModel = Immutable.fromJS(defaultPatientCreationModel);
    }

    public SetDefaultPatientBannerModel(): void {
        this.patientBannerModel = Immutable.fromJS(this.defaultPatientBannerModel);
    }

    /********************************************************************************
       DATA LOOKUP AND MANIPULATION
     *******************************************************************************/

    public FindEmployee(id: string): Employee {
        var employee = cacheStore.GetEmployeeByContactIdOrDefault(id);
        if (employee == null) { employee = new Employee(); }
        return employee;
    }

    public GetEmployeesFromSelectedSites_PrintDialog(): Immutable.List<EmployeeUIModel> {
        var checkedSites = this.helper.GetCheckedItemsFromICheckableList(this.calendarPrintByDoctorModel.get("Sites"));

        var allClinicians = this.calendarPrintByDoctorModel.get("EmployeesGlobalCache");

        var result: EmployeeUIModel[] = [];
        for (var i = 0; i < checkedSites.size; i++) {
            for (var j = 0; j < allClinicians.size; j++) {
                var clinician = allClinicians.get(j);
                var sites = clinician.Sites;
                var hasSite = sites.filter(item => item === checkedSites.get(i).Id).length > 0;
                if (hasSite) {
                    var toAdd = result.filter(i => i.Id === clinician.Id).length === 0;
                    if (toAdd) { result.push(clinician); } // prevent duplicates due to many-to-many relationship between clinicians and sites
                }
            }
        }
        return Immutable.List(result);
    }

    public GetEmployeesBasedOnSitesAndQuery_PrintDialog(): Immutable.List<EmployeeUIModel> {
        var cliniciansFromSites: Immutable.List<EmployeeUIModel> = this.GetEmployeesFromSelectedSites_PrintDialog();
        var filterString = this.calendarPrintByDoctorModel.get("EmployeesSearchText").trim();

        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }

        cliniciansFromSites = Immutable.List<EmployeeUIModel>(cliniciansFromSites.map(x => {
            var emp = x.clone();

            emp.Target = CheckboxTarget.Myle_Calendar_PrintByDoctor_EmployeeItem;
            return emp;
        }));

        return cliniciansFromSites;
    }

    public GetRoomsToDisplay(site: string = null): Immutable.List<Room> {
        return Immutable.List(this.GetRoomsForAppointmentSite(site).toArray().filter(x => x !== null).map(x => {
            if (x.Model)
                return x.Model;
            else {
                return new RoomUIModel();
            }
        }));
    }

    public GetRoomsForAppointmentSite(site: string): Immutable.List<RoomUIModel> {
        site = (site) ? site : this.appointmentEditModel.get("SelectedSiteId");

        if (site) {
            var cachedSite = cacheStore.GetSiteById(site);
            if (cachedSite != null) {
                var result: RoomUIModel[] = [];

                if (cachedSite.Model.Rooms && cachedSite.Model.Rooms.length > 0) {
                    for (let room of cachedSite.Model.Rooms) {
                        var roomModel = new RoomUIModel();
                        roomModel.Id = room.Name;
                        roomModel.Model = room;
                        roomModel.Name = room.Name;
                        result.push(roomModel);
                    }
                }

                return Immutable.List(result);
            }
        }

        return Immutable.List([]);
    }

    public GetRoomsFromSelectedSitesInDisplaySettings(): Immutable.List<RoomUIModel> {
        var checkedSites = this.helper.GetCheckedItemsFromICheckableList(this.calendarDisplaySettingsModel.get("Sites")).toArray();
        var allRooms = this.calendarDisplaySettingsModel.get("Rooms").toArray();

        if (allRooms != null && allRooms.length > 0) {
            var result: RoomUIModel[] = [];

            for (let site of checkedSites) {
                for (let room of allRooms) {
                    if (room.Model.SiteId == site.Id) {
                        result.push(room);
                    }
                }
            }
        }

        return Immutable.List(result);
    }

    public GetSelectedAppointmentState(): FlatLookupView {
        var selectedAppointmentCode = this.appointmentEditModel.get("SelectedAppointmentStateCode");

        if (selectedAppointmentCode) {
            var appointmentState = this.appointmentEditModel.get("AppointmentStates").toArray().filter(x => x.LookupCode == selectedAppointmentCode);
            if (appointmentState && appointmentState.length > 0)
                return appointmentState[0];
        }

        return null;
    }

    public GetDefaultServicesBySiteAndEmployee(selectedSite: string, selectedEmployee: Employee): ServiceModel[] {
        var result: ServiceModel[] = [];

        if (selectedEmployee && selectedSite) {
            var services: ServiceModel[] = selectedEmployee.ServicesOffered
                .filter(x => x.ServiceDefinitionModel.Model.SiteId === selectedSite && x.ServiceDefinitionModel.IsAdHoc == false);
            result = services;
        }
        return result;
    }

    public LoadSitesFromCache(): void {
        var list: SiteUIModel[] = [];
        var listForPrintDialog: SiteUIModel[] = [];

        for (let composite of cacheStore.SiteCache) {
            var site = SiteUIModel.FromCompositeObject(composite);
            site.Checked = true;
            site.Target = CheckboxTarget.Myle_Calendar_SiteItem;
            list.push(site);

            var clone = site.clone();
            clone.Target = CheckboxTarget.Myle_Calendar_PrintByDoctor_SiteItem;
            listForPrintDialog.push(clone);
        }

        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("Sites", Immutable.List(list));
    }

    public LoadRoomsFromCache(): RoomUIModel[] {
        var a: RoomUIModel[] = [];

        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("Rooms", Immutable.List(a));
        return a;
    }

    public LoadEmployeesFromCache(): void {
        var list: EmployeeUIModel[] = [];
        for (let composite of cacheStore.EmployeeCache) {
            var employee = EmployeeUIModel.fromCompositeObject(composite);
            employee.Target = CheckboxTarget.Myle_Calendar_EmployeeItem;
            if (employee.Type === EmployeeType.Practician)
                list.push(employee);
        }

        list.map(x => {
            var m: EmployeeUIModel = x.clone();
            m.Target = CheckboxTarget.Myle_Calendar_PrintByDoctor_EmployeeItem;
            return m;
        });

        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("EmployeesGlobalCache", Immutable.List(list));
    }

    public SetRecurrenceTypes(): void {
        var types: RecurrenceType[] = [];

        types.push({ RecurrenceType: EnumRecurrenceType.OneTime, Description: cacheStore.GetString("UniqueLabel") });
        types.push({ RecurrenceType: EnumRecurrenceType.Daily, Description: cacheStore.GetString("DailyLabel") });
        types.push({ RecurrenceType: EnumRecurrenceType.WeekDay, Description: cacheStore.GetString("WeekdaysLabel") });
        types.push({ RecurrenceType: EnumRecurrenceType.Weekly, Description: cacheStore.GetString("WeeklyLabel") });
        types.push({ RecurrenceType: EnumRecurrenceType.Monthly, Description: cacheStore.GetString("MonthlyLabel") });
        types.push({ RecurrenceType: EnumRecurrenceType.Yearly, Description: cacheStore.GetString("AnnuallyLabel") });

        this.recurrenceTypes = types;
    }

    /**
     this deals with appointment that have been cut to remove original appointment without doing full calendar reload
    **/
    public ManageCutAppointment(): void {
        var appointmentCut = this.calendarModel.get("AppointmentEditForCopy");
        if (appointmentCut && appointmentCut.get("IsFromCutAppointment")) {
            var calendarAppointments = this.calendarModel.get("Appointments").toArray();
            var cutAppointment = appointmentCut.get("Appointment");

            if (cutAppointment && cutAppointment.AppointmentId) {
                var newCalendarList = calendarAppointments.filter(x => x.AppointmentId != cutAppointment.AppointmentId);
                this.calendarModel = this.calendarModel.set("Appointments", Immutable.List<MyleModelsResponseCalendarAppointment>(newCalendarList));
            }
        }
    }

    public ShowCalendarTabForSpecificEmployee(responsibleProviderId: string): void {
        var self = this;
        window.setTimeout(
            () => {
                self.calendarDisplaySettingsModel = self.calendarDisplaySettingsModel.set("EmployeesGlobalCache", self.helper.ChangeCheckStateFromAllICheckable(self.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), false));
                self.calendarDisplaySettingsModel = self.calendarDisplaySettingsModel.set("EmployeesFiltered", self.helper.ChangeCheckStateFromAllICheckable(self.calendarDisplaySettingsModel.get("EmployeesFiltered"), false));
                self.calendarDisplaySettingsModel = self.calendarDisplaySettingsModel.set("EmployeesGlobalCache", self.helper.FindUpdateICheckableItem<EmployeeUIModel>(self.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), responsibleProviderId, true));

                cacheStore.UserSettings.CalendarDisplay = CalendarDisplay.FromModel(self.calendarDisplaySettingsModel);

                self.calendarHandler.CalendarViewMonthClick(null, self);
                self.SetCalendarEmployees();

                RouterHistory.push("calendar");
                self.EmitChangeToListeners();
            },
            0
        );
    }

    public PrepareModelsForViewMount(): void {
        var q = this.calendarDisplaySettingsModel.get("EmployeesSearchText");
        this.LoadSitesFromCache();
        this.LoadEmployeesFromCache();
        this.LoadRoomsFromCache();
        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("EmployeesFiltered", CalendaHelper.GetEmployeesBasedOnSitesAndQuery(q));
        this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("RoomsFiltered", this.GetRoomsFromSelectedSitesInDisplaySettings());

        sidebarStore.Model = sidebarStore.Model.set("IsAdvancedSearchView", false);

        if (cacheStore.UserSettings != null && cacheStore.UserSettings.CalendarDisplay != null) {
            this.LoadCalendarDisplaySettings(cacheStore.UserSettings.CalendarDisplay);
        }
        else {
            // fresh user, auto select it in the search settings
            this.calendarDisplaySettingsModel = this.calendarDisplaySettingsModel.set("EmployeesGlobalCache", this.helper.FindUpdateICheckableItems(this.calendarDisplaySettingsModel.get("EmployeesGlobalCache"), [sessionStore.individualContactId], true));
        }

        this.SetCalendarEmployees();
    }

    public GetPatientNameForPrinting(): string {
        var name = "";
        // first try to get patient info from active patient banner
        if (calendarStore.patientBannerModel != null) {
            var patientModel = this.patientBannerModel.get("Model");
            if (patientModel != null) {
                if (patientModel.IndividualResponse) {
                    name = NameHelper.formatFullNameForHasName(patientModel.IndividualResponse);
                }
            }
        }

        // get freetext or name straight from appointment if no info in patient banner model
        if (name == null || name.trim().length == 0) {
            name = this.appointmentEditModel.get("PatientSearchString");
        }

        if (name != null && name.length > 0) {
            name = name.replaceAll("undefined", "");
            if (name.trim().replaceAll(",", "").trim().length == 0)
                name = "";
            if (name.trim().indexOf(",") == 0) // remove comma if starts with comma
                name = name.replace(",", "").trim();
        }

        return name;
    }

    private InitializeDefaultModels(): void {
        var defaultCalendarDisplayModel = {
            IsAllSitesChecked: false,
            IsAllEmployeesChecked: false,
            IsAllRoomsChecked: false,
            IsAllEquipmentChecked: false,
            Sites: [],
            EmployeesGlobalCache: [],
            EmployeesFiltered: [],
            EmployeesSearchText: "",
            Rooms: [],
            Equipments: [],
            ShowOnlySelectedSite: false,
            PatientsAreVisible: true,
            StatusAreVisible: false,
            PhonesAreVisible: false,
            RoomsAreVisible: false,
            ContactPointsAreVisible: false,
            IncludeCanceled: false,
            IncludeNAM: true,
            TimeSlotLength: 5,
            Service5minAreVisible: false,
            SitesAreVisible: false,
            OneLineViewSelected: false,
            SaveSettingsSelected: false,
            SelectedCalendarTimeResolution: 15,
            CalendarTimeResolutions: [5, 10, 15, 30]

        };

        var defaultCalendarModel = {
            ShowCalendarDisplaySettings: false,
            IsCalendarDayView: true,
            IsCalendarWeekView: false,
            IsCalendarMonthView: false,
            CurrentPageDateDisplay: moment().format("dddd, LL"),
            CalendarStartDate: moment().startOf("day").toDate(),
            CalendarEndDate: moment().add(1, "day").startOf("day").toDate(),
            DateToRememberDayView: moment().startOf("day").toDate(),
            Appointments: [],
            AppointmentsByEmployee: [],
            AppointmentsStateLookups: [],
            PredefinedAppointments: [],
            PredefinedRuleSets: [],
            PredefinedAppointmentsByEmployee: [],
            AllEmployees: [],
            DisplayedEmployees: [],
            InitialLoadDone: false,
            PreventRefreshOnMount: false,
            ScrollPosition: -1,
            ShowComboAppointmentModificationPopup: false,
            OpenAlreadyBookedPopup: false
        };

        var defaultCalendarPrintByDoctorModel = {
            Sites: [],
            EmployeesGlobalCache: [],
            EmployeesFiltered: [],
            EmployeesSearchText: "",
            SelectedPrintOption: "Date"

        };

        var defaultCalendarPrintDialogModel = {
            SelectedPrintOption: "Date"
        };

        let defaultCalendarRulesetModel = {
            RulesetForEdit: [],
            SelectedComboServiceList: [],
            SelectedRuleset: null,
            IsRulesetPopupOpen: false,
            IsAddServicePopupOpen: false,
            IsRulesetCollisionPopupOpen: false,
            HasRulesetModificationFailed: false,
            ExistingRuleDate: [],
            IsStartOver: false
        };

        this.calendarModel = Immutable.fromJS(defaultCalendarModel);
        this.calendarDisplaySettingsModel = Immutable.fromJS(defaultCalendarDisplayModel);
        this.calendarPrintByDoctorModel = Immutable.fromJS(defaultCalendarPrintByDoctorModel);
        this.calendarPrintDialogModel = Immutable.fromJS(defaultCalendarPrintDialogModel);
        this.calendarRulesetModel = Immutable.fromJS(defaultCalendarRulesetModel);
        this.SetDefaultEditAppointmentModel();
        this.SetDefaultPatientModel();
        this.patientPortalModel = new Person();
    }

    public clearPatientToPreloadOnNextAppCreation(): void {
        this.patientToPreloadOnNextAppCreation = null;
        this.subject = "";
    }

    public resetNotificationContactPoint(): void {
        let patient = this.patientBannerModel?.get("Model");

        if (!patient)
            return;

        patient.Emails?.forEach(x => x.isSelectedForNotification = false);
        patient.Phones?.forEach(x => x.isSelectedForNotification = false);

        this.patientBannerModel = this.patientBannerModel.set("Model", patient);
        this.appointmentEditModel = this.appointmentEditModel
            .set("ContactPointNotificationList", [].toImmutable())
            .set("HasNotificationContactPointSpecified", false);
    }

    public setNotificationsByNotifyPatientStatus(isNotificationEnabled: boolean): void {
        let patient = calendarStore.appointmentEditModel.get("SelectedPatient")?.get(0);
        if (!patient) {
            calendarStore.appointmentEditModel = calendarStore.appointmentEditModel
                .set("ContactPointNotificationList", [].toImmutable())
                .set("HasNotificationContactPointSpecified", false);

            return;
        }

        let contactPointNotificationIds = [];
        let cellType = cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE");

        this.setContactIsSelectedForNotification(patient.Phones?.filter(phone => phone.PhoneTypeLookupId == cellType.LookupID), contactPointNotificationIds, isNotificationEnabled);
        this.setContactIsSelectedForNotification(patient.Emails, contactPointNotificationIds, isNotificationEnabled);
        this.setContactIsSelectedForNotification(patient.getRepresentativesEmails(), contactPointNotificationIds, isNotificationEnabled);
        this.setContactIsSelectedForNotification(patient.getRepresentativesPhones().filter(phone => phone.PhoneTypeLookupId == cellType.LookupID), contactPointNotificationIds, isNotificationEnabled);

        calendarStore.appointmentEditModel = calendarStore.appointmentEditModel
            .set("ContactPointNotificationList", contactPointNotificationIds.distinct().toImmutable());

        calendarStore.appointmentEditModel = calendarStore.appointmentEditModel
            .set("SendToAllPersonalContacts", isNotificationEnabled && !contactPointNotificationIds.any());
    }

    private setContactIsSelectedForNotification(contacts: INotificationContactPoint[], contactPointNotificationIds: string[], isChecked: boolean): void {
        contacts?.forEach(contact => {
            contact.isSelectedForNotification = isChecked && contact.NotificationsEnabled;

            if (contact.isSelectedForNotification)
                contactPointNotificationIds.push(contact.ContactPoint_id);
        });
    }

    public checkNotifyPatientStatus(): void {
        const canNotify = cacheStore.portalConfiguration?.HasEmailNotificationEnabled || !!cacheStore.portalConfiguration?.HasSmsNotificationEnabled;

        this.appointmentEditModel = this.appointmentEditModel.set("NotifyPatient", canNotify);
    }

    public handlePatientNotifications(patient: Patient, patientBannerModel: IPatientBannerModel = this.patientBannerModel): void {
        if (!patient?.PatientResponse)
            return;

        // logic to determine the if the selected service is notification enabled
        // because a service can be selected before a patient has been selected
        let selectedServiceHasNotificationEnabled = false;
        if (this.appointmentEditModel.get("SelectedSubjectService")) {
            selectedServiceHasNotificationEnabled = true;

            let wasNotifyAlreadyEnabled = this.appointmentEditModel.get("Appointment") != null
                ? this.appointmentEditModel.get("Appointment").IsPatientNotificationsEnabled
                : false;

            if (wasNotifyAlreadyEnabled != true && this.appointmentEditModel.get("SelectedSubjectService")?.ServiceDefinitionModel?.Model?.IsNotifyPatientDisabledByDefault == true)
                selectedServiceHasNotificationEnabled = false;
            else
                this.appointmentEditModel = this.appointmentEditModel.set("NotifyPatient", selectedServiceHasNotificationEnabled);
        }

        this.patientBannerModel = patientBannerModel;

        const isNotifyPatientChecked = calendarStore.appointmentEditModel.get("NotifyPatient");
        const hasNotifiableContactPoints = calendarStore.appointmentEditModel.get("ContactPointNotificationList")?.count();

        if (!(isNotifyPatientChecked && hasNotifiableContactPoints))
            calendarStore.setNotificationsByNotifyPatientStatus(!!calendarStore.appointmentEditModel.get("Questionnaire") || isNotifyPatientChecked);
    }

    // When all contactPoint possibilities are checked, dont add anything to the list, set flag to false
    // the portal notification system will get all the contactpoints from the patient
    // because the hasNotificationContactPointSpecified is set to false
    public getAppointmentContactPointNotification(
        patient: Person | Patient,
        notificationContactPointList: string[] = []
    ): AppointmentContactPointNotification {
        let appointmentContactPoint = new AppointmentContactPointNotification();
        if (!patient) {
            appointmentContactPoint.contactPointNotificationList = [];
            appointmentContactPoint.hasNotificationContactPointSpecified = false;
            return appointmentContactPoint;
        }

        let cellType = cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE");
        let notifiableContactPointCount =
            patient.Emails?.length +
            patient.Phones?.filter(x => x.PhoneTypeLookupId == cellType.LookupID).length;

        if (notificationContactPointList.length < notifiableContactPointCount) {
            appointmentContactPoint.contactPointNotificationList = notificationContactPointList;
            appointmentContactPoint.hasNotificationContactPointSpecified = true;
        }
        else {
            appointmentContactPoint.contactPointNotificationList = [];
            appointmentContactPoint.hasNotificationContactPointSpecified = false;
        }
        appointmentContactPoint.contactPointNotificationList = notificationContactPointList;

        return appointmentContactPoint;
    }

    private patientHasNotifiableContactPoint(): boolean {
        let patient = this.appointmentEditModel.get("SelectedPatient")?.toArray()?.firstOrDefault();
        let cellType = cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE");

        if (!patient)
            return;

        return patient.Emails?.any() || patient.Phones?.filter(x => x.PhoneTypeLookupId == cellType.LookupID).any();
    }

    private getQuestionnaireStatusInfoFromQuestionnaireAssociation(serviceDefinitionId: string): QuestionnaireStatusInfo {
        if (!serviceDefinitionId)
            return null;

        const service = cacheStore.ServiceDefinitionCache.toArray().firstOrDefault(v => v.Model.Id === serviceDefinitionId);

        return service.Model.QuestionnaireId && new QuestionnaireStatusInfo({
            QuestionnaireInstanceId: GuidHelper.Empty(),
            QuestionnaireId: service.Model.QuestionnaireId
        });
    }
}

export { CalendarStore, defaultInstance as calendarStore };
